import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FileSearch, 
  BookOpen, 
  MessageCircle,
  Camera,
  Crown,
  ArrowRight,
  Sparkles,
  History,
  Lock
} from 'lucide-react';
import LeonAnimated from '../components/LeonAnimated';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Les 4 piliers de l'application V1
const mainFeatures = [
  {
    id: 'analyze',
    title: 'Analyser un devis',
    description: 'Détecte incohérences, oublis et points de vigilance',
    icon: FileSearch,
    color: 'blue',
    to: '/analyze',
    gradient: 'from-blue-500/20 to-blue-600/10',
    borderColor: 'border-blue-500/30',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
    freeLimit: 3,  // 3 analyses offertes pour les utilisateurs gratuits
    usageKey: 'analyses'
  },
  {
    id: 'recipes',
    title: 'Créer une recette travaux',
    description: 'Guide pas à pas avec étapes et matériaux',
    icon: BookOpen,
    color: 'emerald',
    to: '/recipes',
    gradient: 'from-emerald-500/20 to-emerald-600/10',
    borderColor: 'border-emerald-500/30',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400',
    freeLimit: 999,  // Illimité pour tous
    usageKey: 'recipes'
  },
  {
    id: 'diagnostic',
    title: 'Diagnostic photo',
    description: 'Analyse visuelle avec recommandations',
    icon: Camera,
    color: 'purple',
    to: '/diagnostic',
    gradient: 'from-purple-500/20 to-purple-600/10',
    borderColor: 'border-purple-500/30',
    iconBg: 'bg-purple-500/20',
    iconColor: 'text-purple-400',
    freeLimit: 999,  // Illimité pour tous
    usageKey: 'diagnostics'
  },
  {
    id: 'chat',
    title: 'Poser une question',
    description: 'Assistant travaux disponible 24h/24',
    icon: MessageCircle,
    color: 'gold',
    to: '/chat',
    gradient: 'from-gold-500/20 to-amber-500/10',
    borderColor: 'border-gold-500/30',
    iconBg: 'bg-gold-500/20',
    iconColor: 'text-gold-400',
    freeLimit: 999,  // Illimité pour tous
    usageKey: 'chat_messages'
  }
];

export default function DashboardV1() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.subscription_plan === 'premium';

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/usage/stats`, { 
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.log('Error fetching usage: ' + String(error?.message || error));
    } finally {
      setLoading(false);
    }
  };

  const getFirstName = () => {
    if (!user?.name) return '';
    const nameParts = user.name.split(' ');
    if (nameParts[0].toLowerCase() === 'utilisateur') return '';
    return nameParts[0];
  };

  const isFeatureLocked = (feature) => {
    if (isPremium) return false;
    if (feature.freeLimit >= 999) return false;  // Illimité pour tous
    const currentUsage = usage?.[feature.usageKey] || 0;
    return currentUsage >= feature.freeLimit;
  };

  const getUsageText = (feature) => {
    if (isPremium) return 'Illimité';
    if (feature.freeLimit >= 999) return 'Illimité';  // Illimité pour les gratuits aussi
    const currentUsage = usage?.[feature.usageKey] || 0;
    return `${currentUsage}/${feature.freeLimit} utilisé${currentUsage > 1 ? 's' : ''}`;
  };

  const handleFeatureClick = (feature, e) => {
    if (isFeatureLocked(feature)) {
      e.preventDefault();
      navigate('/subscription');
    }
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header avec Léon */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 sm:p-8 mb-8 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gold-500/5 via-transparent to-gold-500/5" />
          <div className="relative flex flex-col sm:flex-row items-center gap-6">
            <LeonAnimated size={80} animate={true} showBadge={isPremium} />
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2">
                Bonjour {getFirstName()} <span className="wave-emoji">👷</span>
              </h1>
              <p className="text-zinc-400 text-lg">
                Moi c'est <span className="text-gold-500 font-semibold">Léon</span>, ton copilote travaux.
              </p>
              <p className="text-zinc-500 mt-1">
                Que veux-tu faire aujourd'hui ?
              </p>
            </div>
            
            {/* Badge Premium ou CTA */}
            {isPremium ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold-500/20 to-amber-500/20 rounded-full border border-gold-500/30">
                <Crown className="w-5 h-5 text-gold-500" />
                <span className="text-gold-400 font-semibold">Premium</span>
              </div>
            ) : (
              <Link 
                to="/subscription"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold-500 to-amber-500 text-black font-bold rounded-full hover:from-gold-400 hover:to-amber-400 transition-all hover:-translate-y-0.5 shadow-lg shadow-gold-500/25"
              >
                <Crown className="w-5 h-5" />
                <span>Passer Premium</span>
              </Link>
            )}
          </div>
        </motion.div>

        {/* Les 4 piliers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {mainFeatures.map((feature, index) => {
            const locked = isFeatureLocked(feature);
            const Icon = feature.icon;
            
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
              >
                <Link
                  to={feature.to}
                  onClick={(e) => handleFeatureClick(feature, e)}
                  className={`group relative block glass-card p-6 sm:p-8 border ${feature.borderColor} hover:border-opacity-60 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${locked ? 'opacity-75' : ''}`}
                  data-testid={`feature-card-${feature.id}`}
                >
                  {/* Gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  
                  {/* Lock overlay for free users */}
                  {locked && (
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10">
                      <div className="text-center">
                        <Lock className="w-8 h-8 text-gold-500 mx-auto mb-2" />
                        <span className="text-gold-400 font-semibold text-sm">Passer Premium</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="relative">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-2xl ${feature.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-heading font-bold text-white mb-2 group-hover:text-gold-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                      {feature.description}
                    </p>
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        isPremium 
                          ? 'bg-gold-500/20 text-gold-400' 
                          : locked 
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-zinc-700/50 text-zinc-400'
                      }`}>
                        {getUsageText(feature)}
                      </span>
                      <ArrowRight className={`w-5 h-5 ${feature.iconColor} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all`} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Historique rapide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Link
            to="/historique"
            className="group glass-card-dark p-4 sm:p-5 flex items-center justify-between hover:border-zinc-600 transition-colors"
            data-testid="history-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center">
                <History className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Mon historique</h3>
                <p className="text-zinc-500 text-sm">Retrouvez vos analyses, recettes et diagnostics</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-gold-400 group-hover:translate-x-1 transition-all" />
          </Link>
        </motion.div>

        {/* Conseil de Léon (simplifié) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-6"
        >
          <div className="glass-card p-5 border-gold-500/20 bg-gradient-to-r from-gold-500/5 via-transparent to-transparent">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-gold-500" />
              </div>
              <div>
                <p className="text-gold-500 font-semibold text-sm mb-1">Conseil de Léon</p>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  Vérifie toujours que l'artisan possède une assurance décennale valide avant de signer un devis.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
